import { colors } from '@lmc/common/constants'
import { NoDataFound } from '../../../../components/generic'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { history } from '../../../../store'
import { Carer, Skill, CarerSkill, SkillNeedTemplate } from '@lmc/ts-common'
import { withRouter } from '../../../../withRouter'
import {
  connect,
  MapDispatchToPropsParam,
  MapStateToPropsParam,
} from 'react-redux'
import Operations from '../../../../operations'
import { RouterProps } from '@lmc/common/types'
import { find } from 'lodash'
import {
  CarerSkillsHeaderSection,
  CarerSkillsModal,
  CarerSkillsTable,
} from './components'
import { DeleteSkillNeedModal } from '../../../AccountsSettings/SkillsAndNeeds/components'
import { Text } from '@lmc/ui-kit'
import { getCarerSkills } from 'selectors/NeedsAndSkills'

export type SkillOptionType = { template: SkillNeedTemplate } & Skill

export type CarerSkillOption = { name: string; skills: SkillOptionType[] }

interface StateProps {
  skills?: CarerSkill[]
  carerSkillOptions?: CarerSkillOption[]
}

interface DispatchProps {
  fetchCarerSkillOptions: () => Promise<unknown>
  fetchCarerSkills: (id: string) => Promise<unknown>
  addSkill: (skill: CarerSkill, id: string) => Promise<unknown>
  updateSkill: (
    skill: CarerSkill,
    id: string,
    skillId: string
  ) => Promise<unknown>
  deleteSkill: (skillId: string, carerId: string) => Promise<unknown>
  router: RouterProps
}

type Props = StateProps & DispatchProps

export function CarerSkillsScreen({
  fetchCarerSkillOptions,
  fetchCarerSkills,
  addSkill,
  updateSkill,
  deleteSkill,
  carerSkillOptions,
  skills,
  router,
}: Props): React.ReactElement {
  const { t } = useTranslation()

  const handleView = () =>
    history.push(history.location.pathname.replace('/skills', '/profile'))

  const carerId = router.params.id

  const [open, setOpen] = useState(false)

  const [loading, setLoading] = useState(true)

  const [openDelete, setOpenDelete] = useState(false)

  const [carerSkillDetail, setCarerSkillDetail] = useState<CarerSkill>()

  const getCarerSkills = async () => {
    await fetchCarerSkills(carerId).then(() => {
      setLoading(false)
    })
  }

  const handleToggleModal = () => {
    if (open) {
      setCarerSkillDetail(undefined)
    }
    setOpen(!open)
  }

  const handleToogleDeleteModal = () => {
    setOpenDelete(!openDelete)
    setOpen(false)
  }

  useEffect(() => {
    fetchCarerSkillOptions()
    getCarerSkills()
  }, [carerId])

  const hasSkills = !!skills?.length

  const handleDelete = async () => {
    try {
      const res = await deleteSkill?.(carerSkillDetail?.id as any, carerId)
      if (res) {
        setCarerSkillDetail(undefined)
        handleToogleDeleteModal()
        handleToggleModal()
        getCarerSkills()
        setOpen(false)
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
  }

  const handleSkillDetails = (id: string) => {
    setCarerSkillDetail(skills?.find((skill) => skill.id === id) as any)
  }

  const skillsFlattenOptions: any[] = []

  carerSkillOptions?.forEach((item) =>
    item.skills?.forEach((skill) => skillsFlattenOptions.push(skill))
  )

  return (
    <>
      <DeleteSkillNeedModal
        isModalVisible={openDelete}
        handleToogleDeleteModal={handleToogleDeleteModal}
        onConfirm={handleDelete}
        title={t('skillsAndNeeds.areYouSureSkill')}
      >
        <Text as="p2">
          {t('skillsAndNeeds.byRemovingSkill', {
            variable: 'team members',
          })}
        </Text>
      </DeleteSkillNeedModal>
      <CarerSkillsModal
        isModalVisible={open}
        handleToggleModal={handleToggleModal}
        handleToogleDeleteModal={handleToogleDeleteModal}
        addSkill={addSkill}
        updateSkill={updateSkill}
        carerSkillOptions={carerSkillOptions}
        skillsFlattenOptions={skillsFlattenOptions}
        skills={skills}
        carerSkillDetail={carerSkillDetail}
        carerId={carerId}
      />
      <div style={styles.container}>
        <CarerSkillsHeaderSection
          handleView={handleView}
          handleToggleModal={handleToggleModal}
        />

        {hasSkills ? (
          <CarerSkillsTable
            handleSkillDetails={handleSkillDetails}
            skillsFlattenOptions={skillsFlattenOptions as any}
            skills={skills}
            loading={loading}
          />
        ) : (
          <NoDataFound
            heading={t(`skillsAndNeeds.nothingToSee`)}
            text={t(`skillsAndNeeds.addSkillsToProfile`)}
            linkText={t(`skillsAndNeeds.findOutMore`)}
            url="https://support.logmycare.co.uk/en/articles/7052004-how-to-add-your-team-s-skills-and-training"
          />
        )}
      </div>
    </>
  )
}

const styles = {
  blankState: {
    marginTop: 20,
    marginBottom: 20,
  },
  container: {
    margin: '0 auto',
    maxWidth: 1386,
    width: '100%',
    padding: '48px 24px',
    paddingTop: 0,
  },
  iconHandle: {
    rotate: '180deg',
  },
  detailLeft: {
    display: 'inline-flex',
    gap: '.5rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    margin: '0 0 24px 0',
  },
  linkColor: {
    color: colors.lightpink,
  },
}

export const mapStateToProps = (
  state: {
    global: {
      home: {
        preferredPeopleNominative: string
      }
    }
    data: {
      carers: Carer[]
      skills: CarerSkill[]
    }
  },
  props: { router: { params: { id: string } } }
) => {
  const {
    router: {
      params: { id },
    },
  } = props

  return {
    carerSkillOptions: (state as any).data?.['skill-need-templates/categories'],
    carer: find(state.data.carers, { id }),
    preferredPeopleNominative: state.global.home.preferredPeopleNominative,
    skills: getCarerSkills(state, props),
  }
}

const mapDispatchToProps = (dispatch: (arg0: unknown) => unknown) => ({
  fetchCarerSkillOptions: () =>
    dispatch(
      Operations.loadList('skill-need-templates/categories', { skills: 1 })
    ),
  fetchCarerSkills: (id: string) =>
    dispatch(
      Operations.loadList('skills', '', true, `/api/list/carers/${id}/skills`)
    ),
  addSkill: (skill: CarerSkill, id: string) =>
    dispatch(
      Operations.createListItem({
        listId: `skills`,
        itemId: '',
        json: skill,
        url: `list/carers/${id}/skills`,
      })
    ),
  updateSkill: (skill: CarerSkill, id: string, skillId?: string) =>
    dispatch(
      Operations.patchListItem({
        listId: `skills`,
        itemId: '',
        json: skill,
        url: `list/carers/${id}/skills/${skillId}`,
      })
    ),

  deleteSkill: (skillId: string, residentId: string) =>
    dispatch(
      Operations.deleteListItem({
        listId: `skills`,
        itemId: '',
        url: `list/carers/${residentId}/skills/${skillId}`,
      })
    ),
})

export default withRouter(
  connect<StateProps, DispatchProps, Props>(
    mapStateToProps as MapStateToPropsParam<StateProps, Props, unknown>,
    mapDispatchToProps as unknown as MapDispatchToPropsParam<
      DispatchProps,
      Props
    >
  )(CarerSkillsScreen)
)
